/**
 * AR Playbook Student Authentication Helpers
 *
 * Centralized helpers for student token management.
 * This module handles the multi-book token registry system
 * where students can have tokens for multiple books.
 *
 * For single-book operations, use getCurrentToken().
 * For multi-book operations, use findTokenForBook().
 */

import { STUDENT_TOKEN } from 'apis/arPlaybookBaseQuery'
import { generateSlug } from 'modules/arplaybook/helper/slugandsubdomain'

import { parseJwt } from 'utils/helper'

import {
    getCurrentToken,
    getTokenRegistry,
    getTokenForBook,
    registerToken,
    removeToken,
    migrateLegacyToken,
    migrateLegacyTokenOnLoad,
    clearAllTokens,
    getAllTokens,
    setCurrentBook,
    parseTokenInfo,
    isTokenRegistered,
    STUDENT_TOKEN_REGISTRY,
    type ITokenRegistryEntry,
    type ITokenRegistry,
} from '../../utils/studentTokenRegistry'

// Re-export registry functions for convenience
export {
    getCurrentToken,
    getTokenRegistry,
    getTokenForBook,
    registerToken,
    removeToken,
    migrateLegacyToken,
    migrateLegacyTokenOnLoad,
    clearAllTokens,
    getAllTokens,
    setCurrentBook,
    parseTokenInfo,
    isTokenRegistered,
    STUDENT_TOKEN_REGISTRY,
    type ITokenRegistryEntry,
    type ITokenRegistry,
}

interface StudentJWTPayload {
    data?: {
        studentId?: string
        bookName?: string
        bookSlug?: string
        businessSlug?: string
        businessUserId?: string
        licenseKey?: string
    }
    exp?: number
}

/**
 * Get student token from localStorage
 *
 * For multi-book access, prefer using:
 * - getCurrentToken() for the currently active book
 * - getTokenForBook(businessSlug, bookSlug) for a specific book
 * - findTokenForBook(businessSlug, bookSlug) with legacy fallback
 *
 * This function returns the legacy STUDENT_TOKEN for backwards compatibility.
 * FIXME: Deprecate after Jan 2027 when legacy tokens expire
 */
export const getStudentToken = (): string | null => {
    // First check the current token from registry
    const currentToken = getCurrentToken()
    if (currentToken) {
        return currentToken
    }

    // Fallback to legacy token
    // FIXME: Remove after Jan 2027
    return localStorage.getItem(STUDENT_TOKEN)
}

/**
 * Remove student token
 *
 * For multi-book access, prefer using removeToken(businessSlug, bookSlug)
 * This clears all tokens for backwards compatibility.
 */
export const removeStudentToken = (): void => {
    clearAllTokens()
}

/**
 * Clear only the legacy STUDENT_TOKEN without affecting the registry.
 * Use this when redirecting to login but wanting to preserve access to other books.
 * FIXME: Remove after Jan 2027 when legacy tokens expire
 */
export const clearLegacyToken = (): void => {
    localStorage.removeItem(STUDENT_TOKEN)
}

/**
 * Set student token
 *
 * For multi-book access, prefer using registerToken(token, businessSlug, bookSlug, bookName)
 * This uses the legacy key for backwards compatibility.
 *
 * @param token - JWT token to store
 */
export const setStudentToken = (token: string): void => {
    localStorage.setItem(STUDENT_TOKEN, token)
}

/**
 * Parse student JWT payload from token
 */
export const parseStudentToken = (token: string): StudentJWTPayload | null => {
    try {
        return JSON.parse(atob(token.split('.')[1])) as StudentJWTPayload
    } catch {
        return null
    }
}

/**
 * Check if student is currently logged in (has valid token)
 */
export const isStudentLoggedIn = (): boolean => {
    const token = getStudentToken()
    if (!token) {
        return false
    }

    // Check token expiration
    const payload = parseStudentToken(token)
    if (payload?.exp) {
        const currentTime = Math.floor(Date.now() / 1000)
        return payload.exp > currentTime
    }

    return true
}

/**
 * Get student data from stored token
 */
export const getStudentData = (): StudentJWTPayload['data'] | null => {
    const token = getStudentToken()
    if (!token) {
        return null
    }

    const payload = parseStudentToken(token)
    return payload?.data ?? null
}

/**
 * Get student ID from token
 */
export const getStudentId = (): string | null => {
    const data = getStudentData()
    return data?.studentId ?? null
}

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
    const payload = parseStudentToken(token)
    if (!payload?.exp) {
        return false // No expiration, assume valid
    }
    const currentTime = Math.floor(Date.now() / 1000)
    return payload.exp <= currentTime
}

// ============================================================================
// Token Lookup with Fallback
// ============================================================================

/**
 * Find the appropriate token for accessing a book
 *
 * Priority:
 * 1. Check registry for businessSlug/bookSlug match
 * 2. If no businessSlug in URL (legacy), check legacy token
 * 3. If legacy token's book matches URL, use it (and optionally migrate)
 *
 * FIXME: Remove legacy STUDENT_TOKEN fallback after Jan 2027
 *
 * @param businessSlug - Business slug from URL (undefined for legacy URLs)
 * @param bookSlug - Book slug from URL
 * @returns Token if found and valid for this book, null otherwise
 */
export const findTokenForBook = (
    businessSlug: string | undefined,
    bookSlug: string,
): { token: string; isLegacy: boolean } | null => {
    // Case 1: New URL format with businessSlug
    if (businessSlug) {
        const registryToken = getTokenForBook(businessSlug, bookSlug)
        if (registryToken) {
            return { token: registryToken, isLegacy: false }
        }
    }

    const token = getStudentToken()
    if (!token) {
        return null
    }

    try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { data } = parseJwt(token)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const tokenBookName = data?.bookName as string | undefined
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const tokenBookSlug = data?.bookSlug as string | undefined
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const tokenBusinessSlug = data?.businessSlug as string | undefined

        // Check if legacy token matches the book being accessed
        const urlBookSlug = generateSlug(bookSlug)
        const legacyBookSlug = tokenBookSlug ?? (tokenBookName ? generateSlug(tokenBookName) : null)

        if (legacyBookSlug && legacyBookSlug === urlBookSlug) {
            // For legacy tokens, if businessSlug is provided in URL but token has businessSlug,
            // verify they match
            if (businessSlug && tokenBusinessSlug && businessSlug !== tokenBusinessSlug) {
                return null // Business mismatch
            }

            return { token, isLegacy: true }
        }
    } catch {
        // Token parsing failed
        console.error('Failed to parse legacy token')
    }

    return null
}
